import { Fragment, type ReactNode } from 'react';

/**
 * Matches a single `[label](url)` link with an `http(s)://` URL.
 *
 * Intentionally conservative: we only honor `http(s)` URLs and keep
 * labels free of `]` so a stray bracket in plain prose can't accidentally
 * swallow text into a link. Descriptions sourced from the secret
 * registry are author-controlled, so a small surface keeps the renderer
 * predictable (no full markdown semantics, no auto-linking, no nested
 * formatting). Anything outside the pattern flows through as text.
 */
const INLINE_LINK_PATTERN = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;

/**
 * Render a short prose string that may contain `[label](https://…)`
 * markdown-style links as a React fragment. Used by secret/description
 * surfaces (Admin → Secrets row, the secret-input dialog, the onboarding
 * wizard) so a registry entry like
 * `'Create yours at [platform.runloop.ai/settings](https://platform.runloop.ai/settings).'`
 * renders the host text as a clickable affordance instead of a literal
 * markdown fragment.
 *
 * For anything more than a single inline link we have `MarkdownContent`
 * — this helper is intentionally markdown-lite so descriptions stay one
 * line and so we don't pull `react-markdown` into surfaces that just
 * need an anchor tag.
 */
export function renderInlineLinks(text: string): ReactNode {
  if (!text.includes('[')) return text;

  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  // `RegExp.exec` advances `lastIndex` across matches because the regex
  // is declared with the `g` flag; reset it so repeated calls with the
  // same input stay deterministic.
  INLINE_LINK_PATTERN.lastIndex = 0;
  while ((match = INLINE_LINK_PATTERN.exec(text)) !== null) {
    const [full, label, href] = match;
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    nodes.push(
      <a
        key={`${match.index}-${href}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-2 hover:text-primary/80"
        onClick={(event) => event.stopPropagation()}
      >
        {label}
      </a>,
    );
    lastIndex = match.index + full.length;
  }
  if (lastIndex === 0) return text;
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return (
    <>
      {nodes.map((node, idx) => (
        <Fragment key={idx}>{node}</Fragment>
      ))}
    </>
  );
}
